import { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import { apiFetch } from '../../../shared/lib/apiClient';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_NOTIFICATION_EMAILS = 7;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const STATUS_META = {
  scheduled:      { label: 'Scheduled',  dot: 'bg-sky-400',     text: 'text-sky-700',     bg: 'bg-sky-50',     border: 'border-sky-100'     },
  overdue:        { label: 'Overdue',     dot: 'bg-red-500',     text: 'text-red-700',     bg: 'bg-red-50',     border: 'border-red-100'     },
  partially_paid: { label: 'Partial',     dot: 'bg-amber-400',   text: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-100'   },
  fully_paid:     { label: 'Paid',        dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-100' },
};

const FLAG_OPTIONS = [
  { value: 'none',          label: 'No flag'       },
  { value: 'follow_up',     label: 'Follow up'     },
  { value: 'cash_risk',     label: 'Cash risk'     },
  { value: 'vendor_called', label: 'Called vendor'  },
  { value: 'dispute',       label: 'Dispute'       },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtCompact = (n) => {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
};

const toDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const parseDateKey = (dk) => {
  if (!dk) return null;
  const [y, m, d] = dk.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const addMonths = (date, n) => new Date(date.getFullYear(), date.getMonth() + n, 1);
const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);
const startOfWeek = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return d;
};
const endOfWeek = (date) => {
  const s = startOfWeek(date);
  s.setDate(s.getDate() + 6);
  return s;
};
const addDays = (date, days) => {
  const n = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  n.setDate(n.getDate() + days);
  return n;
};

const todayKey = () => toDateKey(new Date());

const fmtDateLong = (dk) => {
  const d = parseDateKey(dk);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const CalendarView = ({ loaded }) => {
  const { companies, activeCompany } = useAuth();

  const companyOptions = useMemo(() =>
    companies.map((e) => ({ id: e.companyId, name: e.company?.name || 'Unnamed company' })),
    [companies]
  );

  // State
  const [paymentsByCompany, setPaymentsByCompany] = useState({});
  const [localCompanyId, setLocalCompanyId] = useState(companyOptions[0]?.id || null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [selectedDateKey, setSelectedDateKey] = useState(todayKey());
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  const [detailNoteDraft, setDetailNoteDraft] = useState('');
  const [detailFlagDraft, setDetailFlagDraft] = useState('none');
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [detailSaveMessage, setDetailSaveMessage] = useState('');

  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationEmails, setNotificationEmails] = useState([]);
  const [notificationEmailDraft, setNotificationEmailDraft] = useState('');
  const [notificationPhone, setNotificationPhone] = useState('');
  const [notificationPhoneDraft, setNotificationPhoneDraft] = useState('');
  const [notificationError, setNotificationError] = useState('');
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [reminderStep, setReminderStep] = useState(0); // 0 = overview, 1 = setup

  // Filters
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVendor, setFilterVendor] = useState('all');
  const [filterMinAmount, setFilterMinAmount] = useState('');
  const [filterMaxAmount, setFilterMaxAmount] = useState('');

  const hasActiveFilters = filterSearch || filterStatus !== 'all' || filterVendor !== 'all' || filterMinAmount || filterMaxAmount;

  const clearAllFilters = () => {
    setFilterSearch('');
    setFilterStatus('all');
    setFilterVendor('all');
    setFilterMinAmount('');
    setFilterMaxAmount('');
  };

  // Company
  useEffect(() => {
    if (!activeCompany?.companyId && !localCompanyId && companyOptions[0]?.id)
      setLocalCompanyId(companyOptions[0].id);
  }, [companyOptions, activeCompany, localCompanyId]);

  const selectedCompanyId = activeCompany?.companyId || localCompanyId || companyOptions[0]?.id || '';

  // Load payments
  const fetchPayments = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    setLoadError('');
    try {
      const res = await apiFetch(`/api/calendar/payments?${new URLSearchParams({ source: 'auto', limit: '1000' })}`, { companyId: selectedCompanyId });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setPaymentsByCompany((p) => ({ ...p, [selectedCompanyId]: data.payments || [] }));
      setConflicts(data.conflicts || []);
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // Load notification settings
  useEffect(() => {
    if (!selectedCompanyId) return;
    let cancelled = false;
    apiFetch('/api/calendar/settings', { companyId: selectedCompanyId })
      .then(r => r.json().catch(() => ({})))
      .then(data => {
        if (cancelled) return;
        const s = data.settings || {};
        setNotificationsEnabled(!!s.notificationsEnabled);
        setNotificationEmails(Array.isArray(s.notificationEmails) ? s.notificationEmails : []);
        setNotificationPhone(s.notificationPhone || '');
        setNotificationPhoneDraft(s.notificationPhone || '');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedCompanyId]);

  // Derived data
  const payments = useMemo(() => paymentsByCompany[selectedCompanyId] || [], [paymentsByCompany, selectedCompanyId]);
  const today = todayKey();

  const selectedPayment = useMemo(() => payments.find((p) => p.id === selectedPaymentId) || null, [payments, selectedPaymentId]);
  const selectedSavedNote = selectedPayment ? (selectedPayment.quickNote ?? selectedPayment.notes ?? '') : '';
  const selectedSavedFlag = selectedPayment ? (selectedPayment.flag ?? 'none') : 'none';
  const hasUnsavedDetailChanges = Boolean(selectedPayment && (detailNoteDraft !== selectedSavedNote || detailFlagDraft !== selectedSavedFlag));

  useEffect(() => {
    if (!selectedPayment) { setDetailNoteDraft(''); setDetailFlagDraft('none'); setDetailSaveMessage(''); return; }
    setDetailNoteDraft(selectedSavedNote);
    setDetailFlagDraft(selectedSavedFlag);
    setDetailSaveMessage('');
  }, [selectedPayment, selectedSavedNote, selectedSavedFlag]);

  // Unique vendor names for filter dropdown
  const vendorOptions = useMemo(() => {
    const names = [...new Set(payments.map(p => p.partyName).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    return names;
  }, [payments]);

  // Apply filters then group by date (paid bills always filtered out)
  const filteredPayments = useMemo(() => {
    let list = payments.filter(p => p.status !== 'fully_paid');

    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      list = list.filter(p =>
        (p.partyName || '').toLowerCase().includes(q) ||
        (p.invoiceNumber || '').toLowerCase().includes(q)
      );
    }
    if (filterStatus !== 'all') {
      list = list.filter(p => p.status === filterStatus);
    }
    if (filterVendor !== 'all') {
      list = list.filter(p => p.partyName === filterVendor);
    }
    if (filterMinAmount) {
      const min = parseFloat(filterMinAmount);
      if (!isNaN(min)) list = list.filter(p => p.amount >= min);
    }
    if (filterMaxAmount) {
      const max = parseFloat(filterMaxAmount);
      if (!isNaN(max)) list = list.filter(p => p.amount <= max);
    }

    return list;
  }, [payments, filterSearch, filterStatus, filterVendor, filterMinAmount, filterMaxAmount]);

  // Group filtered payments by due date
  const paymentsByDate = useMemo(() => {
    const map = {};
    filteredPayments.forEach(p => {
      if (!map[p.dueDate]) map[p.dueDate] = [];
      map[p.dueDate].push(p);
    });
    Object.values(map).forEach(arr =>
      arr.sort((a, b) => {
        if (a.status === 'overdue' && b.status !== 'overdue') return -1;
        if (b.status === 'overdue' && a.status !== 'overdue') return 1;
        return b.amount - a.amount;
      })
    );
    return map;
  }, [filteredPayments]);

  // Summary stats
  const stats = useMemo(() => {
    const ws = toDateKey(startOfWeek(new Date()));
    const we = toDateKey(endOfWeek(new Date()));
    const open = payments.filter(p => p.status !== 'fully_paid');
    const thisWeek = open.filter(p => p.dueDate >= ws && p.dueDate <= we);
    const overdue = open.filter(p => p.status === 'overdue');
    const thisMonth = open.filter(p => {
      const ms = toDateKey(startOfMonth(new Date()));
      const me = toDateKey(endOfMonth(new Date()));
      return p.dueDate >= ms && p.dueDate <= me;
    });

    return {
      weekCount: thisWeek.length,
      weekAmount: thisWeek.reduce((s, p) => s + p.outstandingAmount, 0),
      overdueCount: overdue.length,
      overdueAmount: overdue.reduce((s, p) => s + p.outstandingAmount, 0),
      monthCount: thisMonth.length,
      monthAmount: thisMonth.reduce((s, p) => s + p.outstandingAmount, 0),
    };
  }, [payments]);

  // Calendar grid cells
  const calendarCells = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(currentMonth));
    const gridEnd = endOfWeek(endOfMonth(currentMonth));
    const cells = [];
    for (let d = new Date(gridStart); d <= gridEnd; d = addDays(d, 1)) {
      cells.push(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
    }
    return cells;
  }, [currentMonth]);

  // Payments for selected date sidebar
  const selectedDatePayments = useMemo(() => {
    return paymentsByDate[selectedDateKey] || [];
  }, [paymentsByDate, selectedDateKey]);

  // Handlers
  const handleRefresh = async () => {
    if (!selectedCompanyId) return;
    setIsRefreshing(true);
    setLoadError('');
    try {
      const res = await apiFetch('/api/calendar/refresh', { method: 'POST', body: JSON.stringify({ source: 'auto' }), companyId: selectedCompanyId });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Refresh failed');
      setPaymentsByCompany((p) => ({ ...p, [selectedCompanyId]: data.payments || [] }));
      setConflicts(data.conflicts || []);
    } catch (e) { setLoadError(e.message); }
    finally { setIsRefreshing(false); }
  };

  const handleSaveDetails = async () => {
    if (!selectedPayment || !hasUnsavedDetailChanges || isSavingDetails) return;
    setIsSavingDetails(true);
    setDetailSaveMessage('');
    try {
      const res = await apiFetch(`/api/calendar/payments/${encodeURIComponent(selectedPayment.id)}/details`, {
        method: 'PUT', body: JSON.stringify({ note: detailNoteDraft, flag: detailFlagDraft }), companyId: selectedCompanyId,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setPaymentsByCompany((prev) => ({
        ...prev,
        [selectedCompanyId]: (prev[selectedCompanyId] || []).map((p) =>
          p.id === selectedPayment.id ? { ...p, notes: detailNoteDraft, quickNote: detailNoteDraft, flag: detailFlagDraft } : p
        ),
      }));
      if (Array.isArray(data.conflicts)) setConflicts(data.conflicts);
      setDetailSaveMessage('Saved');
    } catch (e) { setLoadError(e.message); }
    finally { setIsSavingDetails(false); }
  };

  const handleAddNotificationEmail = () => {
    const next = notificationEmailDraft.trim().toLowerCase();
    if (!next) return;
    if (notificationEmails.length >= MAX_NOTIFICATION_EMAILS) { setNotificationError(`Max ${MAX_NOTIFICATION_EMAILS} emails.`); return; }
    if (!EMAIL_PATTERN.test(next)) { setNotificationError('Enter a valid email.'); return; }
    if (notificationEmails.includes(next)) { setNotificationError('Already added.'); return; }
    setNotificationEmails((p) => [...p, next]);
    setNotificationEmailDraft('');
    setNotificationError('');
  };

  const persistNotificationSettings = async (payload) => {
    if (!selectedCompanyId) return false;
    setIsSavingNotifications(true);
    setNotificationError('');
    try {
      const res = await apiFetch('/api/calendar/settings', { method: 'PUT', body: JSON.stringify(payload), companyId: selectedCompanyId });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      const s = data.settings || {};
      setNotificationsEnabled(!!s.notificationsEnabled);
      setNotificationEmails(Array.isArray(s.notificationEmails) ? s.notificationEmails : []);
      return true;
    } catch (e) { setNotificationError(e.message); return false; }
    finally { setIsSavingNotifications(false); }
  };

  const goToToday = () => {
    setCurrentMonth(startOfMonth(new Date()));
    setSelectedDateKey(today);
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  // Right panel: reminders > filters > detail > day list
  const rightPanelContent = remindersOpen ? 'reminders' : filterDrawerOpen ? 'filters' : selectedPayment ? 'detail' : 'dayList';

  return (
    <div className="space-y-3 sm:space-y-4">

      {/* Header row */}
      <div
        className="flex items-center justify-between gap-3 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '150ms' }}
      >
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-semibold text-zinc-900">Payment Calendar</h1>
          <p className="text-xs sm:text-sm text-zinc-400 mt-0.5">Know when every bill is due</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {/* Reminders button */}
          <button
            onClick={() => { setRemindersOpen(!remindersOpen); setFilterDrawerOpen(false); setSelectedPaymentId(null); setReminderStep(notificationsEnabled ? 0 : 0); }}
            className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors text-[11px] font-medium ${
              remindersOpen
                ? 'bg-zinc-900 border-zinc-900 text-white'
                : notificationsEnabled
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-white border-zinc-200 text-zinc-500 hover:border-zinc-400 hover:text-zinc-700'
            }`}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 6a4 4 0 0 1 8 0c0 4 2 5 2 5H2s2-1 2-5" /><path d="M6.5 13a1.5 1.5 0 0 0 3 0" />
            </svg>
            {notificationsEnabled ? 'Reminders on' : 'Reminders'}
          </button>

          <div className="flex items-center gap-1.5">
            <button
              onClick={() => { setFilterDrawerOpen(!filterDrawerOpen); setRemindersOpen(false); setSelectedPaymentId(null); }}
              className={`relative p-2 rounded-lg border transition-colors ${
                filterDrawerOpen ? 'bg-zinc-900 border-zinc-900 text-white' : 'bg-white border-zinc-200 text-zinc-400 hover:border-zinc-400 hover:text-zinc-600'
              }`}
              title="Filters & settings"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h12M4 6.5h8M6 10h4M7 13.5h2" />
              </svg>
              {hasActiveFilters && !filterDrawerOpen && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-blue-500 border-2 border-white" />
              )}
            </button>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-2 rounded-lg border border-zinc-200 text-zinc-400 hover:border-zinc-400 hover:text-zinc-600 transition-colors disabled:opacity-40"
              title="Refresh"
            >
              {isRefreshing ? (
                <div className="w-4 h-4 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" />
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 2v5h5" /><path d="M2.5 10A6 6 0 1 0 3.2 5L1 7" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {loadError && <p className="text-xs text-red-500">{loadError}</p>}

      {/* Active filter strip */}
      {hasActiveFilters && !filterDrawerOpen && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Filtered:</span>
          {filterSearch && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 text-[11px] text-zinc-600">
              "{filterSearch}"
              <button onClick={() => setFilterSearch('')} className="text-zinc-400 hover:text-zinc-700">
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 3l6 6M9 3l-6 6" /></svg>
              </button>
            </span>
          )}
          {filterStatus !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 text-[11px] text-zinc-600">
              {(STATUS_META[filterStatus] || {}).label || filterStatus}
              <button onClick={() => setFilterStatus('all')} className="text-zinc-400 hover:text-zinc-700">
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 3l6 6M9 3l-6 6" /></svg>
              </button>
            </span>
          )}
          {filterVendor !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 text-[11px] text-zinc-600">
              {filterVendor}
              <button onClick={() => setFilterVendor('all')} className="text-zinc-400 hover:text-zinc-700">
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 3l6 6M9 3l-6 6" /></svg>
              </button>
            </span>
          )}
          {(filterMinAmount || filterMaxAmount) && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 text-[11px] text-zinc-600">
              {filterMinAmount && filterMaxAmount ? `$${filterMinAmount}–$${filterMaxAmount}` : filterMinAmount ? `≥ $${filterMinAmount}` : `≤ $${filterMaxAmount}`}
              <button onClick={() => { setFilterMinAmount(''); setFilterMaxAmount(''); }} className="text-zinc-400 hover:text-zinc-700">
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 3l6 6M9 3l-6 6" /></svg>
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-zinc-700 transition-colors ml-1">
            Clear all
          </button>
        </div>
      )}

      {/* Summary cards */}
      <div
        className="grid grid-cols-3 gap-2 sm:gap-3 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '220ms' }}
      >
        <div className="bg-white border border-zinc-200 rounded-xl p-2.5 sm:p-4 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-medium truncate">Due This Week</p>
          <p className="text-sm sm:text-2xl font-light text-zinc-900 mt-1 tabular-nums truncate">{fmtCompact(stats.weekAmount)}</p>
          <p className="text-[11px] text-zinc-400 mt-0.5">{stats.weekCount} bill{stats.weekCount !== 1 ? 's' : ''}</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-xl p-2.5 sm:p-4 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-medium truncate">Due This Month</p>
          <p className="text-sm sm:text-2xl font-light text-zinc-900 mt-1 tabular-nums truncate">{fmtCompact(stats.monthAmount)}</p>
          <p className="text-[11px] text-zinc-400 mt-0.5">{stats.monthCount} bill{stats.monthCount !== 1 ? 's' : ''}</p>
        </div>
        {stats.overdueCount > 0 ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 sm:p-4 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.15em] text-red-400 font-medium truncate">Overdue</p>
            <p className="text-sm sm:text-2xl font-light text-red-600 mt-1 tabular-nums truncate">{fmtCompact(stats.overdueAmount)}</p>
            <p className="text-[11px] text-red-400 mt-0.5">{stats.overdueCount} bill{stats.overdueCount !== 1 ? 's' : ''}</p>
          </div>
        ) : (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2.5 sm:p-4 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.15em] text-emerald-500 font-medium truncate">Overdue</p>
            <p className="text-sm sm:text-2xl font-light text-emerald-600 mt-1 tabular-nums">$0</p>
            <p className="text-[11px] text-emerald-400 mt-0.5">All clear</p>
          </div>
        )}
      </div>

      {/* Calendar + Right Panel */}
      <div
        className="flex flex-col lg:flex-row gap-3 sm:gap-4 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '340ms' }}
      >

        {/* ─── Calendar Grid ──────────────────────────────────────── */}
        <div className="flex-1 min-w-0">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button
                onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg border border-zinc-200 bg-white flex items-center justify-center text-zinc-400 hover:text-zinc-800 hover:border-zinc-400 transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M8 2L4 6l4 4" /></svg>
              </button>
              <h2 className="text-base sm:text-lg font-semibold text-zinc-900 min-w-0 sm:min-w-[180px] text-center">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <button
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg border border-zinc-200 bg-white flex items-center justify-center text-zinc-400 hover:text-zinc-800 hover:border-zinc-400 transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M4 2l4 4-4 4" /></svg>
              </button>
            </div>
            <button
              onClick={goToToday}
              className="px-2.5 sm:px-3 py-1.5 rounded-lg border border-zinc-200 text-[11px] uppercase tracking-widest text-zinc-500 hover:text-zinc-800 hover:border-zinc-400 transition-colors"
            >
              Today
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-5 h-5 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* ── Mobile: compact month grid ── */}
              <div className="sm:hidden bg-white border border-zinc-200 rounded-2xl overflow-hidden">
                <div className="grid grid-cols-7 border-b border-zinc-100">
                  {['M','T','W','T','F','S','S'].map((d, i) => (
                    <div key={i} className="py-2 text-center text-[9px] uppercase tracking-widest text-zinc-400 font-medium">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {calendarCells.map((cellDate) => {
                    const dk = toDateKey(cellDate);
                    const dayPayments = paymentsByDate[dk] || [];
                    const outside = cellDate.getMonth() !== currentMonth.getMonth();
                    const isToday = dk === today;
                    const isSelected = dk === selectedDateKey;
                    const isPast = dk < today;
                    const hasOverdue = dayPayments.some(p => p.status === 'overdue');
                    const hasBills = dayPayments.length > 0 && !outside;

                    return (
                      <button
                        key={dk}
                        onClick={() => { setSelectedDateKey(dk); setSelectedPaymentId(null); }}
                        className={`relative py-2 flex flex-col items-center border-r border-b border-zinc-100 transition-all duration-150 ${
                          outside ? 'bg-zinc-50/50' :
                          isSelected ? 'bg-blue-50/60 ring-1 ring-inset ring-blue-200' :
                          'bg-white active:bg-zinc-50'
                        }`}
                      >
                        <span className={`text-[11px] font-semibold w-6 h-6 inline-flex items-center justify-center rounded-full ${
                          isToday ? 'bg-zinc-900 text-white' :
                          outside ? 'text-zinc-300' :
                          isPast && hasOverdue ? 'text-red-500' :
                          'text-zinc-600'
                        }`}>
                          {cellDate.getDate()}
                        </span>
                        {hasBills && (
                          <div className="flex items-center gap-0.5 mt-0.5">
                            {dayPayments.slice(0, 3).map((p, i) => {
                              const meta = STATUS_META[p.status] || STATUS_META.scheduled;
                              return <span key={i} className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />;
                            })}
                            {dayPayments.length > 3 && <span className="text-[7px] text-zinc-400">+</span>}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Desktop: full month grid ── */}
              <div className="hidden sm:block bg-white border border-zinc-200 rounded-2xl overflow-hidden">
                <div className="grid grid-cols-7 border-b border-zinc-100">
                  {DAYS.map((d) => (
                    <div key={d} className="py-2.5 text-center text-[10px] uppercase tracking-widest text-zinc-400 font-medium">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {calendarCells.map((cellDate) => {
                    const dk = toDateKey(cellDate);
                    const dayPayments = paymentsByDate[dk] || [];
                    const outside = cellDate.getMonth() !== currentMonth.getMonth();
                    const isToday = dk === today;
                    const isSelected = dk === selectedDateKey;
                    const isPast = dk < today;

                    const hasOverdue = dayPayments.some(p => p.status === 'overdue');
                    const dayTotal = dayPayments.reduce((s, p) => s + p.outstandingAmount, 0);

                    return (
                      <button
                        key={dk}
                        onClick={() => { setSelectedDateKey(dk); setSelectedPaymentId(null); }}
                        className={`relative min-h-[88px] border-r border-b border-zinc-100 p-1.5 text-left transition-all duration-150 ${
                          outside ? 'bg-zinc-50/50' :
                          isSelected ? 'bg-blue-50/60 ring-1 ring-inset ring-blue-200' :
                          'bg-white hover:bg-zinc-50/60'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-0.5">
                          <span className={`text-[11px] font-semibold w-6 h-6 inline-flex items-center justify-center rounded-full transition-all ${
                            isToday ? 'bg-zinc-900 text-white' :
                            outside ? 'text-zinc-300' :
                            isPast && hasOverdue ? 'text-red-500' :
                            'text-zinc-600'
                          }`}>
                            {cellDate.getDate()}
                          </span>
                          {dayPayments.length > 0 && !outside && (
                            <span className={`text-[9px] tabular-nums font-semibold ${
                              hasOverdue ? 'text-red-500' : 'text-zinc-400'
                            }`}>
                              {fmtCompact(dayTotal)}
                            </span>
                          )}
                        </div>
                        {!outside && dayPayments.length > 0 && (
                          <div className="space-y-px">
                            {dayPayments.slice(0, 2).map((p) => {
                              const meta = STATUS_META[p.status] || STATUS_META.scheduled;
                              return (
                                <div
                                  key={p.id}
                                  className={`flex items-center gap-1 px-1 py-0.5 rounded text-[9px] leading-tight truncate ${
                                    p.status === 'overdue' ? 'bg-red-50 text-red-600' :
                                    p.status === 'partially_paid' ? 'bg-amber-50 text-amber-600' :
                                    'bg-zinc-50 text-zinc-500'
                                  }`}
                                >
                                  <span className={`w-1 h-1 rounded-full shrink-0 ${meta.dot}`} />
                                  <span className="truncate">{p.partyName}</span>
                                </div>
                              );
                            })}
                            {dayPayments.length > 2 && (
                              <span className="text-[8px] text-zinc-400 pl-1">+{dayPayments.length - 2} more</span>
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* ─── RIGHT PANEL (Filters, Bill detail, or Day list) ──── */}
        <div className="w-full lg:w-[340px] shrink-0">
          <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden lg:sticky lg:top-4">

            {rightPanelContent === 'filters' ? (
              /* ── Filter & Settings Panel ── */
              <div className="flex flex-col max-h-[calc(100vh-200px)]">
                <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between shrink-0">
                  <div>
                    <h2 className="text-sm font-semibold text-zinc-900">Filters & Settings</h2>
                    <p className="text-[10px] text-zinc-400 mt-0.5">
                      {hasActiveFilters ? `${filteredPayments.length} payment${filteredPayments.length !== 1 ? 's' : ''} match` : 'Narrow down your calendar'}
                    </p>
                  </div>
                  <button
                    onClick={() => setFilterDrawerOpen(false)}
                    className="w-7 h-7 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center transition-colors"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M3 3l6 6M9 3l-6 6" /></svg>
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-5">
                  {/* ── Filters section ── */}
                  <div className="space-y-3">
                    {/* Search */}
                    <div>
                      <label className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium block mb-1.5">Search</label>
                      <div className="relative">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-300">
                          <circle cx="7" cy="7" r="4.5" /><path d="M10.5 10.5L14 14" />
                        </svg>
                        <input
                          type="text"
                          value={filterSearch}
                          onChange={(e) => setFilterSearch(e.target.value)}
                          placeholder="Vendor, invoice #"
                          className="w-full pl-8 pr-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-[12px] text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-900 transition-shadow"
                        />
                      </div>
                    </div>

                    {/* Status */}
                    <div>
                      <label className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium block mb-1.5">Status</label>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          { value: 'all', label: 'All' },
                          { value: 'scheduled', label: 'Scheduled' },
                          { value: 'overdue', label: 'Overdue' },
                          { value: 'partially_paid', label: 'Partial' },
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            onClick={() => setFilterStatus(opt.value)}
                            className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
                              filterStatus === opt.value
                                ? 'bg-zinc-900 text-white'
                                : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Vendor */}
                    <div>
                      <label className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium block mb-1.5">Vendor</label>
                      <select
                        value={filterVendor}
                        onChange={(e) => setFilterVendor(e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-[12px] text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-900 appearance-none cursor-pointer"
                      >
                        <option value="all">All vendors</option>
                        {vendorOptions.map((v) => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    </div>

                    {/* Amount range */}
                    <div>
                      <label className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium block mb-1.5">Amount range</label>
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-300 text-[11px]">$</span>
                          <input
                            type="number"
                            value={filterMinAmount}
                            onChange={(e) => setFilterMinAmount(e.target.value)}
                            placeholder="Min"
                            className="w-full pl-6 pr-2 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-[12px] text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-900 tabular-nums"
                          />
                        </div>
                        <span className="text-zinc-300 text-xs">–</span>
                        <div className="relative flex-1">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-300 text-[11px]">$</span>
                          <input
                            type="number"
                            value={filterMaxAmount}
                            onChange={(e) => setFilterMaxAmount(e.target.value)}
                            placeholder="Max"
                            className="w-full pl-6 pr-2 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-[12px] text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-900 tabular-nums"
                          />
                        </div>
                      </div>
                    </div>

                    {hasActiveFilters && (
                      <button
                        onClick={clearAllFilters}
                        className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-zinc-700 transition-colors"
                      >
                        Clear all filters
                      </button>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-zinc-100 shrink-0">
                  <button
                    onClick={() => setFilterDrawerOpen(false)}
                    className="w-full px-4 py-2 bg-zinc-900 text-white text-[10px] uppercase tracking-widest rounded-lg hover:bg-zinc-800 transition-colors text-center"
                  >
                    Done
                  </button>
                </div>
              </div>

            ) : rightPanelContent === 'reminders' ? (
              /* ── Reminders Setup Panel ── */
              <div className="flex flex-col max-h-[calc(100vh-200px)]">
                <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-zinc-900 flex items-center justify-center">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 6a4 4 0 0 1 8 0c0 4 2 5 2 5H2s2-1 2-5" /><path d="M6.5 13a1.5 1.5 0 0 0 3 0" />
                      </svg>
                    </div>
                    <h2 className="text-sm font-semibold text-zinc-900">Reminders</h2>
                  </div>
                  <button
                    onClick={() => setRemindersOpen(false)}
                    className="w-7 h-7 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center transition-colors"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M3 3l6 6M9 3l-6 6" /></svg>
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {!notificationsEnabled && notificationEmails.length === 0 && notificationPhone === '' ? (
                    /* ── Onboarding: no reminders set up yet ── */
                    <div className="p-5 space-y-5">
                      <div className="text-center py-4">
                        <div className="w-12 h-12 rounded-2xl bg-zinc-100 flex items-center justify-center mx-auto mb-3">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-400">
                            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 8 3 8H3s3-1 3-8" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
                          </svg>
                        </div>
                        <h3 className="text-sm font-semibold text-zinc-900">Never miss a payment</h3>
                        <p className="text-[12px] text-zinc-400 mt-1 leading-relaxed max-w-[240px] mx-auto">
                          Get notified before bills are due and when payments go overdue.
                        </p>
                      </div>

                      {/* Channel cards */}
                      <div className="space-y-2">
                        <button
                          onClick={() => setReminderStep(1)}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-zinc-200 hover:border-zinc-400 transition-colors text-left group"
                        >
                          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500">
                              <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 7L2 7" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-medium text-zinc-900">Email</p>
                            <p className="text-[11px] text-zinc-400">Add up to {MAX_NOTIFICATION_EMAILS} recipients</p>
                          </div>
                          <svg width="14" height="14" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-zinc-300 group-hover:text-zinc-500 transition-colors"><path d="M4 2l4 4-4 4" /></svg>
                        </button>

                        <button
                          onClick={() => setReminderStep(2)}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-zinc-200 hover:border-zinc-400 transition-colors text-left group"
                        >
                          <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-medium text-zinc-900">SMS</p>
                            <p className="text-[11px] text-zinc-400">Text alerts to your phone</p>
                          </div>
                          <span className="text-[9px] uppercase tracking-widest text-zinc-300 font-medium shrink-0">Soon</span>
                        </button>
                      </div>
                    </div>
                  ) : reminderStep === 1 || (notificationEmails.length > 0 && reminderStep !== 2) ? (
                    /* ── Email setup ── */
                    <div className="p-4 space-y-4">
                      <button
                        onClick={() => setReminderStep(0)}
                        className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-700 transition-colors"
                      >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M8 2L4 6l4 4" /></svg>
                        Back
                      </button>

                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500">
                            <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 7L2 7" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-[13px] font-semibold text-zinc-900">Email reminders</h3>
                          <p className="text-[10px] text-zinc-400">{notificationEmails.length}/{MAX_NOTIFICATION_EMAILS} recipients</p>
                        </div>
                      </div>

                      {/* Email list */}
                      {notificationEmails.length > 0 && (
                        <div className="space-y-1.5">
                          {notificationEmails.map((email) => (
                            <div key={email} className="flex items-center justify-between px-3 py-2 rounded-lg bg-zinc-50 border border-zinc-100">
                              <span className="text-[12px] text-zinc-700 truncate min-w-0">{email}</span>
                              <button onClick={() => {
                                const next = notificationEmails.filter((e) => e !== email);
                                setNotificationEmails(next);
                                if (notificationsEnabled) persistNotificationSettings({ notificationsEnabled: next.length > 0, notificationEmails: next });
                              }} className="text-zinc-300 hover:text-red-500 transition-colors shrink-0 ml-2">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M3 3l6 6M9 3l-6 6" /></svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add email */}
                      <div>
                        <div className="flex gap-1.5">
                          <input
                            type="email"
                            value={notificationEmailDraft}
                            onChange={(e) => setNotificationEmailDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNotificationEmail(); } }}
                            placeholder="name@company.com"
                            className="flex-1 px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-[12px] text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-900 min-w-0"
                          />
                          <button onClick={handleAddNotificationEmail} disabled={notificationEmails.length >= MAX_NOTIFICATION_EMAILS || !notificationEmailDraft.trim()}
                            className="px-3 py-2 bg-zinc-900 text-white text-[10px] uppercase tracking-widest rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-40 shrink-0">
                            Add
                          </button>
                        </div>
                        {notificationError && <p className="text-[10px] text-red-500 mt-1.5">{notificationError}</p>}
                      </div>

                      {/* What you'll get */}
                      <div className="rounded-xl bg-zinc-50 border border-zinc-100 p-3 space-y-2">
                        <p className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium">You'll receive</p>
                        {[
                          { icon: '→', text: '3-day advance notice for upcoming bills' },
                          { icon: '!', text: 'Same-day alert when a bill becomes overdue' },
                          { icon: '✓', text: 'Weekly summary of outstanding payments' },
                        ].map((item) => (
                          <div key={item.text} className="flex items-start gap-2">
                            <span className="w-4 h-4 rounded bg-zinc-200 flex items-center justify-center text-[9px] text-zinc-500 font-bold shrink-0 mt-px">{item.icon}</span>
                            <p className="text-[11px] text-zinc-500 leading-relaxed">{item.text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : reminderStep === 2 ? (
                    /* ── SMS setup (coming soon) ── */
                    <div className="p-4 space-y-4">
                      <button
                        onClick={() => setReminderStep(0)}
                        className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-700 transition-colors"
                      >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M8 2L4 6l4 4" /></svg>
                        Back
                      </button>

                      <div className="text-center py-6">
                        <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-400">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                          </svg>
                        </div>
                        <h3 className="text-sm font-semibold text-zinc-900">SMS coming soon</h3>
                        <p className="text-[12px] text-zinc-400 mt-1.5 leading-relaxed max-w-[220px] mx-auto">
                          We're building SMS alerts so you get a text when bills need attention. Set up email for now.
                        </p>
                        <button
                          onClick={() => setReminderStep(1)}
                          className="mt-4 px-4 py-2 bg-zinc-900 text-white text-[10px] uppercase tracking-widest rounded-lg hover:bg-zinc-800 transition-colors"
                        >
                          Set up email instead
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* ── Overview: reminders already configured ── */
                    <div className="p-4 space-y-3">
                      {/* Email channel */}
                      <div
                        onClick={() => setReminderStep(1)}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl border border-zinc-200 hover:border-zinc-400 transition-colors cursor-pointer group"
                      >
                        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500">
                            <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 7L2 7" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-zinc-900">Email</p>
                          <p className="text-[11px] text-zinc-400">{notificationEmails.length} recipient{notificationEmails.length !== 1 ? 's' : ''}</p>
                        </div>
                        {notificationsEnabled && notificationEmails.length > 0 ? (
                          <span className="text-[9px] uppercase tracking-widest text-emerald-600 font-medium shrink-0">Active</span>
                        ) : (
                          <span className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium shrink-0">Edit</span>
                        )}
                      </div>

                      {/* SMS channel */}
                      <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-zinc-100 bg-zinc-50/50">
                        <div className="w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-400">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-zinc-400">SMS</p>
                          <p className="text-[11px] text-zinc-300">Coming soon</p>
                        </div>
                      </div>

                      {/* Master toggle */}
                      <div className="border-t border-zinc-100 pt-3 flex items-center justify-between">
                        <span className="text-[11px] text-zinc-500">
                          {notificationsEnabled ? 'Reminders are active' : 'Reminders paused'}
                        </span>
                        <button
                          onClick={async () => {
                            if (notificationsEnabled) {
                              await persistNotificationSettings({ notificationsEnabled: false });
                            } else if (notificationEmails.length > 0) {
                              await persistNotificationSettings({ notificationsEnabled: true, notificationEmails });
                            }
                          }}
                          disabled={isSavingNotifications || notificationEmails.length === 0}
                          className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 ${
                            notificationsEnabled ? 'bg-emerald-500' : 'bg-zinc-200'
                          }`}
                        >
                          <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition-transform duration-200 ease-in-out mt-0.5 ${
                            notificationsEnabled ? 'translate-x-4 ml-0.5' : 'translate-x-0 ml-0.5'
                          }`} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer: enable/save for email setup step */}
                {reminderStep === 1 && notificationEmails.length > 0 && (
                  <div className="px-4 py-3 border-t border-zinc-100 shrink-0">
                    <button
                      onClick={async () => {
                        const saved = await persistNotificationSettings({ notificationsEnabled: true, notificationEmails });
                        if (saved) { setReminderStep(0); }
                      }}
                      disabled={isSavingNotifications}
                      className="w-full px-4 py-2.5 bg-zinc-900 text-white text-[11px] uppercase tracking-widest rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-40 text-center flex items-center justify-center gap-2"
                    >
                      {isSavingNotifications ? (
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 8l4 4 6-6" /></svg>
                      )}
                      {notificationsEnabled ? 'Save changes' : 'Enable reminders'}
                    </button>
                  </div>
                )}
              </div>

            ) : rightPanelContent === 'detail' ? (
              /* ── Bill Detail (inline, not overlay) ── */
              <div className="flex flex-col max-h-[calc(100vh-200px)]">
                {/* Back + header */}
                <div className="px-4 py-3 border-b border-zinc-100 shrink-0">
                  <button
                    onClick={() => setSelectedPaymentId(null)}
                    className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-700 transition-colors mb-2"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M8 2L4 6l4 4" /></svg>
                    Back to day
                  </button>
                  <h2 className="text-base font-semibold text-zinc-900">{selectedPayment.partyName}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    {(() => {
                      const meta = STATUS_META[selectedPayment.status] || STATUS_META.scheduled;
                      return (
                        <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border font-medium ${meta.bg} ${meta.text} ${meta.border}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                          {meta.label}
                        </span>
                      );
                    })()}
                    <span className="text-[11px] text-zinc-400">{selectedPayment.invoiceNumber}</span>
                  </div>
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Key figures */}
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Amount', value: fmt(selectedPayment.amount) },
                      { label: 'Outstanding', value: fmt(selectedPayment.outstandingAmount) },
                      { label: 'Due date', value: fmtDateLong(selectedPayment.dueDate) },
                      { label: 'Terms', value: selectedPayment.terms || '-' },
                    ].map(({ label, value }) => (
                      <div key={label} className="bg-zinc-50 rounded-lg p-2.5">
                        <p className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium">{label}</p>
                        <p className="mt-0.5 text-[13px] font-medium text-zinc-900">{value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Line items */}
                  {(selectedPayment.lineItems || []).length > 0 && !(selectedPayment.lineItems.length === 1 && selectedPayment.lineItems[0].description === 'Transaction total') && (
                    <div>
                      <p className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">Line items</p>
                      <div className="border border-zinc-100 rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-zinc-50 border-b border-zinc-100">
                              {['Description', 'Qty', 'Amount'].map((h, i) => (
                                <th key={h} className={`px-2.5 py-1.5 text-[9px] uppercase tracking-widest text-zinc-400 font-medium ${i > 0 ? 'text-right' : 'text-left'}`}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedPayment.lineItems || []).map((line, i) => (
                              <tr key={i} className="border-b border-zinc-50 last:border-0">
                                <td className="px-2.5 py-1.5 text-[12px] text-zinc-700">{line.description}</td>
                                <td className="px-2.5 py-1.5 text-[12px] text-zinc-500 text-right tabular-nums">{line.qty}</td>
                                <td className="px-2.5 py-1.5 text-[12px] text-zinc-700 text-right tabular-nums font-medium">{fmt(line.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Note + flag */}
                  <div className="space-y-2">
                    <div>
                      <label className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium block mb-1">Quick note</label>
                      <textarea
                        value={detailNoteDraft}
                        onChange={(e) => setDetailNoteDraft(e.target.value)}
                        rows={2}
                        placeholder="Called vendor, payment delayed..."
                        className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-[12px] text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-900 resize-none"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <select value={detailFlagDraft} onChange={(e) => setDetailFlagDraft(e.target.value)}
                        className="flex-1 px-2.5 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-[12px] text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-900">
                        {FLAG_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <button onClick={handleSaveDetails} disabled={!hasUnsavedDetailChanges || isSavingDetails}
                        className="px-4 py-2 bg-zinc-900 text-white text-[10px] uppercase tracking-widest rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-40 shrink-0">
                        {isSavingDetails ? '...' : 'Save'}
                      </button>
                    </div>
                    {detailSaveMessage && <span className="text-[10px] text-emerald-600">{detailSaveMessage}</span>}
                  </div>

                  {/* Change history */}
                  {(() => {
                    const conflictsForPayment = conflicts.filter(c => c.paymentId === selectedPayment.id);
                    if (conflictsForPayment.length === 0) return null;
                    return (
                      <div className="border-t border-zinc-100 pt-3">
                        <p className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">Change history</p>
                        <div className="space-y-1.5">
                          {conflictsForPayment.slice(0, 3).map((c) => (
                            <div key={c.id} className="rounded-lg border border-zinc-100 px-2.5 py-1.5">
                              <p className="text-[12px] text-zinc-700">{c.changes.join(' / ')}</p>
                              <p className="text-[9px] text-zinc-400 mt-0.5">{new Date(c.changedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : (
              /* ── Day List ── */
              <>
                {/* Date header */}
                <div className={`px-4 py-3 border-b ${
                  selectedDateKey < today && selectedDatePayments.some(p => p.status === 'overdue')
                    ? 'bg-red-50 border-red-100'
                    : selectedDateKey === today ? 'bg-zinc-50 border-zinc-100'
                    : 'border-zinc-100'
                }`}>
                  <p className={`text-base font-semibold ${
                    selectedDateKey < today && selectedDatePayments.some(p => p.status === 'overdue') ? 'text-red-900' : 'text-zinc-900'
                  }`}>
                    {(() => {
                      const d = parseDateKey(selectedDateKey);
                      if (!d) return '';
                      if (selectedDateKey === today) return 'Today';
                      const tomorrow = toDateKey(addDays(new Date(), 1));
                      if (selectedDateKey === tomorrow) return 'Tomorrow';
                      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    })()}
                  </p>
                  <p className="text-[10px] text-zinc-400 mt-0.5">
                    {fmtDateLong(selectedDateKey)}
                  </p>
                  {selectedDatePayments.length > 0 && (
                    <p className={`text-sm font-medium mt-1.5 tabular-nums ${
                      selectedDatePayments.some(p => p.status === 'overdue') ? 'text-red-600' : 'text-zinc-900'
                    }`}>
                      {fmt(selectedDatePayments.reduce((s, p) => s + p.outstandingAmount, 0))} due
                    </p>
                  )}
                </div>

                {/* Payment list */}
                <div className="max-h-[calc(100vh-340px)] overflow-y-auto">
                  {selectedDatePayments.length === 0 ? (
                    <div className="py-12 text-center">
                      <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-2">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-300">
                          <rect x="3" y="4" width="14" height="13" rx="2" /><path d="M7 2v4M13 2v4M3 9h14" />
                        </svg>
                      </div>
                      <p className="text-xs text-zinc-400">No payments due</p>
                    </div>
                  ) : (
                    <div>
                      {selectedDatePayments.map((payment) => {
                        const meta = STATUS_META[payment.status] || STATUS_META.scheduled;
                        return (
                          <button
                            key={payment.id}
                            onClick={() => setSelectedPaymentId(payment.id)}
                            className="w-full text-left px-4 py-3 hover:bg-zinc-50 transition-colors border-b border-zinc-100 last:border-0"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <span className={`w-2 h-2 rounded-full shrink-0 ${meta.dot}`} />
                                  <p className="text-sm font-medium text-zinc-900 truncate">{payment.partyName}</p>
                                </div>
                                <p className="text-[10px] text-zinc-400 mt-0.5 pl-3.5">{payment.invoiceNumber}</p>
                                {payment.flag && payment.flag !== 'none' && (
                                  <span className="inline-flex items-center gap-1 mt-1 ml-3.5 px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wider bg-amber-50 text-amber-600 border border-amber-100">
                                    {FLAG_OPTIONS.find(f => f.value === payment.flag)?.label || payment.flag}
                                  </span>
                                )}
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-sm font-medium tabular-nums text-zinc-900">{fmt(payment.amount)}</p>
                                {payment.outstandingAmount < payment.amount && (
                                  <p className="text-[10px] text-zinc-400 tabular-nums mt-0.5">{fmt(payment.outstandingAmount)} left</p>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default CalendarView;
